import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@dispo-web/app/services/auth.service';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import {
  CreateRole,
  GrantItem,
  ResponseError,
  Role,
  RoleList,
  UpdateRole,
} from '@tourfold/libraries/tf-api-client-public-v2';
import {
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
  resolveSettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';
import {
  ColumnDef,
  FormFieldErrorComponent,
  SettingsListHeader,
  SettingsLoadingPlaceholder,
  TfColumnDefDirective,
  TfDataTableComponent,
  type TfSegmentedControlOption,
} from '@tourfold/libraries/shared-ui';
import { SignalFormServerErrors } from '@tourfold/libraries/shared-util';

import { provideRolesAiPageContext } from './roles-list.ai-page-context';
import { RolesService } from './roles.service';
import {
  AdvancedFilter,
  AdvancedGrantGroup,
  AdvancedGrantRow,
  SelectOption,
} from './role-editor.models';
import { RoleEditorState, type RoleEditorSnapshot } from './role-editor.state';
import {
  ROLE_PERMISSION_GROUPS,
  PermissionGroupDefinition,
  PermissionGroupId,
  PermissionLevel,
  grantsForGroupFromGrantList,
  grantsForGroupLevel,
  metadataForGrant,
  resolveHighestGroupLevel,
} from './role-permission-groups';
import { RolePermissionLevelSelectorComponent } from './role-permission-level-selector';
import { RolePermissionScopeComponent, RolePermissionScopeSummary } from './role-permission-scope';

export type RoleTypeFilter = 'all' | 'system' | 'custom';

const PERMISSION_LEVEL_VALUES: readonly PermissionLevel[] = ['none', 'read', 'write', 'manage'];
const PERMISSION_LEVEL_SET = new Set<PermissionLevel>(PERMISSION_LEVEL_VALUES);
const CREATE_ROLE_DEFAULT_GROUP_LEVELS = {
  'tour-planning': 'write',
  'field-execution': 'write',
  'document-management': 'write',
  workflows: 'none',
  comments: 'write',
  'custom-objects': 'write',
  'workspace-settings': 'none',
  billing: 'none',
  insights: 'read',
  'users-service-accounts': 'read',
} satisfies Readonly<Record<PermissionGroupId, PermissionLevel>>;

interface RoleTableRow extends Role {
  readonly displayName: string;
  readonly displayDescription: string;
  readonly grantCount: number;
  readonly groupCount: number;
  readonly groupSummaries: readonly RolePermissionScopeSummary[];
}

@Component({
  selector: 'tf-settings-roles-list',
  imports: [
    ButtonModule,
    ConfirmDialogModule,
    FormsModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TooltipModule,
    TranslateModule,
    SettingsInlineMessage,
    SettingsLoadError,
    SettingsListHeader,
    SettingsLoadingPlaceholder,
    FormFieldErrorComponent,
    TfColumnDefDirective,
    TfDataTableComponent,
    RolePermissionLevelSelectorComponent,
    RolePermissionScopeComponent,
  ],
  templateUrl: './roles-list.html',
  styleUrl: './roles-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class Roles {
  private readonly rolesService = inject(RolesService);
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly authService = inject(AuthService);
  private readonly resourceAccessService = inject(ResourceAccessService);

  readonly rolesSearchQuery = input('');
  readonly rolesTypeFilter = input<RoleTypeFilter>('all');
  readonly rolesSearchQueryChange = output<string>();
  readonly rolesTypeFilterChange = output<RoleTypeFilter>();

  readonly permissionGroups = ROLE_PERMISSION_GROUPS;

  readonly roles = signal<Role[]>([]);
  readonly availableGrants = signal<GrantItem[]>([]);
  readonly hasLoadedOnce = signal(false);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);

  readonly editorState = new RoleEditorState(
    (ids) => this.filterAssignableRoleIds(ids),
    () => this.createEmptyDraft()
  );

  // Inline server-side validation errors (RFC 9457 ProblemDetail `errors[]`). The editor binds
  // each field with `[ngModel]` to a separate signal, so we project them into a single model
  // signal for the helper — its effect auto-clears a field's error the moment the user edits it.
  // Pointers come back as `#/name`, `#/assignableByRoles`, `#/grants` (camelCase property names).
  readonly serverErrors = new SignalFormServerErrors(
    computed(() => ({
      name: this.editorState.name(),
      description: this.editorState.description(),
      assignableByRoles: this.editorState.assignableByRoles(),
    })),
    ['name', 'description', 'assignableByRoles']
  );

  readonly canCreateRole = computed(() => this.resourceAccessService.canPerform('roles', 'create'));
  readonly canUpdateRole = computed(() => this.resourceAccessService.canPerform('roles', 'update'));
  readonly canDeleteRole = computed(() => this.resourceAccessService.canPerform('roles', 'delete'));

  readonly isEditorOpen = computed(() => this.editorState.isOpen());

  readonly canEditCurrentRole = computed(() => {
    if (this.editorState.mode() === 'create') {
      return this.canCreateRole();
    }
    return this.canUpdateRole() && (this.editorState.sourceRole()?.editable ?? false);
  });

  readonly roleTypeOptions = computed<SelectOption<RoleTypeFilter>[]>(() => [
    { label: this.translate('SETTINGS_ACCESS.ROLES.FILTERS.ALL_TYPES'), value: 'all' },
    { label: this.translate('SETTINGS_ACCESS.ROLES.FILTERS.SYSTEM'), value: 'system' },
    { label: this.translate('SETTINGS_ACCESS.ROLES.FILTERS.CUSTOM'), value: 'custom' },
  ]);

  readonly columns = computed<ColumnDef[]>(() => [
    {
      field: 'roleInfo',
      title: 'SETTINGS_ACCESS.ROLES.ROLE',
      type: 'custom',
    },
    {
      field: 'memberCount',
      title: 'SETTINGS_ACCESS.ROLES.MEMBERS',
      type: 'number',
      width: '8rem',
    },
    {
      field: 'permissionScope',
      title: 'SETTINGS_ACCESS.ROLES.PERMISSION_SCOPE',
      type: 'custom',
      width: '28rem',
    },
    {
      field: 'actions',
      title: 'SETTINGS_ACCESS.ROLES.ACTIONS',
      type: 'custom',
      width: '10rem',
    },
  ]);

  readonly advancedFilterOptions = computed<SelectOption<AdvancedFilter>[]>(() => [
    { label: this.translate('SETTINGS_ACCESS.ROLES.FILTERS.ALL_GRANTS'), value: 'all' },
    { label: this.translate('SETTINGS_ACCESS.ROLES.FILTERS.ENABLED'), value: 'enabled' },
    { label: this.translate('SETTINGS_ACCESS.ROLES.LEVELS.READ'), value: 'read' },
    { label: this.translate('SETTINGS_ACCESS.ROLES.LEVELS.WRITE'), value: 'write' },
    { label: this.translate('SETTINGS_ACCESS.ROLES.LEVELS.MANAGE'), value: 'manage' },
  ]);

  readonly allRoleOptions = computed<SelectOption[]>(() =>
    this.sortedRoles(this.roles())
      .filter((role) => !this.isOwnerRole(role))
      .map((role) => ({
        label: this.formatRoleName(role.name),
        value: role.id,
        disabled: !role.canAssign,
      }))
  );

  readonly tableRows = computed<RoleTableRow[]>(() =>
    this.sortedRoles(this.roles())
      .filter((role) => this.matchesRoleSearch(role))
      .filter((role) => this.matchesTypeFilter(role))
      .map((role) => this.toTableRow(role))
  );

  readonly advancedGrantGroups = computed<readonly AdvancedGrantGroup[]>(() => {
    const selected = this.editorState.enabledGrantSet();
    const grantItems = this.availableGrants();
    const search = this.editorState.advancedSearchQuery().trim().toLowerCase();
    const filter = this.editorState.advancedFilter();

    return this.permissionGroups
      .map((group) => {
        const groupGrantItems = grantItems
          .filter((grant) => metadataForGrant(grant.name).groupId === group.id)
          .sort((a, b) => a.name.localeCompare(b.name));
        const allRows = groupGrantItems.map((grant) => this.toAdvancedGrantRow(grant, selected));
        const rows = allRows.filter((row) => this.matchesAdvancedGrantFilter(row, search, filter));

        return {
          group,
          rows,
          enabledCount: allRows.filter((row) => row.enabled).length,
          totalCount: allRows.length,
        };
      })
      .filter((group) => group.totalCount > 0 && group.rows.length > 0);
  });

  readonly isSaveDisabled = computed(() => {
    const canSave =
      this.editorState.mode() === 'create' ? this.canCreateRole() : this.canEditCurrentRole();
    return (
      !canSave ||
      !this.editorState.name().trim() ||
      this.isSaving() ||
      !this.editorState.hasChanges()
    );
  });

  constructor() {
    provideRolesAiPageContext({
      roles: this.roles,
      searchQuery: this.rolesSearchQuery,
      editingRole: this.editorState.sourceRole,
      isEditorOpen: this.isEditorOpen,
    });
    void this.loadInitialData();
  }

  onSearchQueryChange(query: string): void {
    this.rolesSearchQueryChange.emit(query);
  }

  onTypeFilterChange(filter: RoleTypeFilter): void {
    this.rolesTypeFilterChange.emit(filter);
  }

  reloadRoles(): void {
    void this.loadInitialData();
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  private resetEditorErrors(): void {
    this.actionError.set(null);
    this.serverErrors.clear();
  }

  openCreateEditor(): void {
    if (!this.canCreateRole()) {
      return;
    }
    this.resetEditorErrors();
    this.editorState.openForCreate(CREATE_ROLE_DEFAULT_GROUP_LEVELS);
  }

  openEditEditor(role: Role): void {
    if (!this.canUpdateRole() || !role.editable) {
      return;
    }
    this.resetEditorErrors();
    this.editorState.openFromRole(role, 'edit');
  }

  duplicateRole(role: Role): void {
    if (!this.canCreateRole()) {
      return;
    }
    const duplicate: Role = {
      ...role,
      id: '',
      name: this.translateService.instant('SETTINGS_ACCESS.ROLES.COPY_NAME', {
        name: this.formatRoleName(role.name),
      }),
      editable: true,
      isSystemRole: false,
      memberCount: 0,
    };
    this.resetEditorErrors();
    this.editorState.openFromRole(duplicate, 'create');
  }

  backToRoles(): void {
    this.editorState.close();
    this.resetEditorErrors();
  }

  discardChanges(): void {
    const source = this.editorState.sourceRole();
    if (!source) {
      return;
    }
    this.editorState.openFromRole(source, this.editorState.mode());
  }

  async saveRole(): Promise<void> {
    if (this.isSaveDisabled()) {
      return;
    }
    const snapshot = this.editorState.currentSnapshot();
    if (!snapshot) {
      return;
    }

    this.isSaving.set(true);
    this.resetEditorErrors();
    const payload = this.toRolePayload(snapshot);

    try {
      if (this.editorState.mode() === 'create') {
        await firstValueFrom(this.rolesService.createRole(payload as CreateRole));
      } else {
        await firstValueFrom(
          this.rolesService.updateRole(
            requireRoleId(this.editorState.sourceRole()),
            payload as UpdateRole
          )
        );
      }
      this.rolesService.notifyRolesUpdated();
      this.editorState.close();
      await this.loadInitialData(false);
    } catch (error: unknown) {
      await this.handleRoleError(
        error,
        this.editorState.mode() === 'create'
          ? 'SETTINGS_ACCESS.ROLES.CREATE_ERROR'
          : 'SETTINGS_ACCESS.ROLES.UPDATE_ERROR'
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  confirmDeleteRole(role: Role | null = this.editorState.sourceRole()): void {
    if (!role?.id || this.isDeleteDisabled(role)) {
      return;
    }
    this.confirmationService.confirm({
      message: this.translate('SETTINGS_ACCESS.ROLES.DELETE_CONFIRMATION'),
      header: this.translate('GENERAL.CONFIRM'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => void this.deleteRole(role),
    });
  }

  selectGroupLevel(groupId: PermissionGroupId, level: PermissionLevel): void {
    if (!this.canEditCurrentRole()) {
      return;
    }
    this.editorState.selectGroupLevel(groupId, level);
  }

  selectGroupLevelValue(groupId: PermissionGroupId, level: string): void {
    if (isPermissionLevel(level)) {
      this.selectGroupLevel(groupId, level);
    }
  }

  groupLevelDisplayValue(group: PermissionGroupDefinition): string {
    return this.editorState.groupDisplayLevels()[group.id];
  }

  groupLevelOptions(group: PermissionGroupDefinition): readonly TfSegmentedControlOption[] {
    return PERMISSION_LEVEL_VALUES.map((level) => {
      const supported = group.levels.includes(level);
      return {
        label: this.levelLabel(level),
        value: level,
        disabled: !supported,
        disabledTooltip: supported
          ? undefined
          : this.translate('SETTINGS_ACCESS.ROLES.LEVEL_UNAVAILABLE'),
      };
    });
  }

  isGroupAdvancedConfigured(group: PermissionGroupDefinition): boolean {
    return this.groupLevelDisplayValue(group) === 'advanced';
  }

  toggleAdvancedGrant(row: AdvancedGrantRow, checked: boolean): void {
    if (!this.canToggleGrant(row)) {
      return;
    }
    this.editorState.toggleAdvancedGrant(row, checked);
  }

  toggleAdvancedGrantGroup(groupId: PermissionGroupId): void {
    this.editorState.toggleAdvancedGrantGroup(groupId);
  }

  isAdvancedGrantGroupExpanded(groupId: PermissionGroupId): boolean {
    return this.editorState.isAdvancedGrantGroupExpanded(groupId);
  }

  canToggleGrant(row: AdvancedGrantRow): boolean {
    return this.canEditCurrentRole() && row.assignable;
  }

  isDeleteDisabled(role: Role | null = this.editorState.sourceRole()): boolean {
    return (
      !role?.editable || !this.canDeleteRole() || this.isSaving() || (role.memberCount ?? 0) > 0
    );
  }

  deleteTooltip(role: Role): string {
    if (role.isSystemRole) {
      return this.translate('SETTINGS_ACCESS.ROLES.SYSTEM_ROLE_CANNOT_BE_DELETED');
    }
    if (!role.editable || !this.canDeleteRole()) {
      return this.translate('SETTINGS_ACCESS.ROLES.NO_PERMISSION');
    }
    if ((role.memberCount ?? 0) > 0) {
      return this.translate('SETTINGS_ACCESS.ROLES.DELETE_BLOCKED_ASSIGNED');
    }
    return this.translate('GENERAL.DELETE');
  }

  levelLabel(level: PermissionLevel): string {
    return this.translate(`SETTINGS_ACCESS.ROLES.LEVELS.${level.toUpperCase()}`);
  }

  grantLevelLabel(level: 'read' | 'write' | 'manage'): string {
    return this.levelLabel(level);
  }

  formatRoleName(roleName: string | undefined): string {
    if (!roleName) {
      return '';
    }
    return roleName
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private async loadInitialData(resetError = true): Promise<void> {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    if (resetError) {
      this.actionError.set(null);
    }
    try {
      const [rolesResponse, grantsResponse] = await Promise.all([
        firstValueFrom(this.rolesService.getRoles()),
        firstValueFrom(this.rolesService.getGrants()),
      ]);
      this.applyRolesResponse(rolesResponse);
      this.availableGrants.set(grantsResponse.grants);
      this.loadError.set(null);
      this.loadErrorDetails.set(null);
      this.initialLoadFailed.set(false);
      this.hasLoadedOnce.set(true);
    } catch (error: unknown) {
      const message = this.translate('SETTINGS.LOAD_ERROR.TITLE');
      if (hadLoadedOnce) {
        this.actionError.set(message);
      } else {
        this.loadError.set(message);
        this.initialLoadFailed.set(true);
        this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  private applyRolesResponse(response: RoleList): void {
    this.roles.set(response.items ?? []);
  }

  private createEmptyDraft(): Role {
    return {
      id: '',
      name: '',
      description: '',
      grants: createDefaultRoleGrants(),
      assignableByRoles: this.getCurrentUserAssignableRoleIds(),
      memberCount: 0,
      canAssign: true,
      editable: true,
      isSystemRole: false,
    };
  }

  private toRolePayload(snapshot: RoleEditorSnapshot): CreateRole | UpdateRole {
    const assignableGrantNames = new Set(
      this.availableGrants()
        .filter((grant) => grant.assignable)
        .map((grant) => grant.name)
    );
    const grants =
      this.editorState.mode() === 'create'
        ? snapshot.grants.filter((grant) => assignableGrantNames.has(grant))
        : snapshot.grants;
    return {
      name: snapshot.name,
      description: snapshot.description || undefined,
      grants: new Set(grants),
      assignableByRoles: new Set(this.filterAssignableRoleIds(snapshot.assignableByRoles)),
    } as CreateRole | UpdateRole;
  }

  private async deleteRole(role: Role): Promise<void> {
    if (!role.id) {
      return;
    }
    this.isSaving.set(true);
    this.actionError.set(null);
    try {
      await firstValueFrom(this.rolesService.deleteRole(role.id));
      this.rolesService.notifyRolesUpdated();
      this.editorState.close();
      await this.loadInitialData(false);
    } catch (error: unknown) {
      await this.handleRoleError(error, 'SETTINGS_ACCESS.ROLES.DELETE_ERROR');
    } finally {
      this.isSaving.set(false);
    }
  }

  private toTableRow(role: Role): RoleTableRow {
    const grants = role.grants ?? [];
    const grantCount = grants.length;
    const groupSummaries = this.permissionGroups.map((group) => {
      const groupGrants = grantsForGroupFromGrantList(group, grants);
      return {
        group,
        grants: groupGrants,
        level: resolveHighestGroupLevel(group, groupGrants),
      };
    });
    return {
      ...role,
      displayName: this.formatRoleName(role.name),
      displayDescription: role.description ?? '',
      grantCount,
      groupCount: groupSummaries.filter((summary) => summary.grants.length > 0).length,
      groupSummaries,
    };
  }

  private sortedRoles(roles: readonly Role[]): Role[] {
    return [...roles].sort((a, b) => {
      if ((a.isSystemRole ?? false) !== (b.isSystemRole ?? false)) {
        return a.isSystemRole ? -1 : 1;
      }
      if (a.isSystemRole && b.isSystemRole) {
        const systemRankDiff = this.systemRoleRank(a) - this.systemRoleRank(b);
        if (systemRankDiff !== 0) {
          return systemRankDiff;
        }
      }
      return this.formatRoleName(a.name).localeCompare(this.formatRoleName(b.name));
    });
  }

  private systemRoleRank(role: Role): number {
    switch (this.formatRoleName(role.name).toLowerCase()) {
      case 'owner':
        return 0;
      case 'admin':
        return 1;
      case 'billing admin':
        return 2;
      default:
        return 3;
    }
  }

  private matchesRoleSearch(role: Role): boolean {
    const query = this.rolesSearchQuery().trim().toLowerCase();
    if (!query) {
      return true;
    }
    return `${role.name ?? ''} ${role.description ?? ''}`.toLowerCase().includes(query);
  }

  private matchesTypeFilter(role: Role): boolean {
    const filter = this.rolesTypeFilter();
    if (filter === 'system') {
      return role.isSystemRole;
    }
    if (filter === 'custom') {
      return !role.isSystemRole;
    }
    return true;
  }

  private toAdvancedGrantRow(grant: GrantItem, selected: ReadonlySet<string>): AdvancedGrantRow {
    const metadata = metadataForGrant(grant.name);
    return {
      grant: grant.name,
      assignable: grant.assignable,
      enabled: selected.has(grant.name),
      level: metadata.level,
      label: metadata.label,
      description: metadata.description,
    };
  }

  private matchesAdvancedGrantFilter(
    row: AdvancedGrantRow,
    search: string,
    filter: AdvancedFilter
  ): boolean {
    if (filter === 'enabled' && !row.enabled) {
      return false;
    }
    if (filter !== 'all' && filter !== 'enabled' && row.level !== filter) {
      return false;
    }
    if (!search) {
      return true;
    }
    return `${row.grant} ${row.label} ${row.description}`.toLowerCase().includes(search);
  }

  private getCurrentUserAssignableRoleIds(): string[] {
    const currentUser = this.authService.currentUser();
    const currentRoles = currentUser?.roles ?? [];
    const normalized = currentRoles.map((role) => role.trim().toLowerCase());
    return this.roles()
      .filter((role) => normalized.includes((role.name ?? '').toLowerCase()))
      .filter((role) => !this.isOwnerRole(role))
      .map((role) => role.id)
      .filter(Boolean);
  }

  private filterAssignableRoleIds(roleIds: readonly string[]): string[] {
    const ownerRoleIds = new Set(
      this.roles()
        .filter((role) => this.isOwnerRole(role))
        .map((role) => role.id)
    );
    return roleIds.filter((roleId) => !ownerRoleIds.has(roleId));
  }

  private isOwnerRole(role: Role): boolean {
    return this.formatRoleName(role.name).toLowerCase() === 'owner';
  }

  private async handleRoleError(error: unknown, fallbackKey: string): Promise<void> {
    // Field-level validation (HTTP 422): bind each error next to its input and route any
    // unmappable messages to the banner.
    const applied = await this.serverErrors.apply(error);
    if (applied && applied.fieldCount > 0) {
      this.actionError.set(
        applied.generalMessages.length > 0 ? applied.generalMessages.join(' ') : null
      );
      return;
    }
    // No per-field errors — surface the top-level ProblemDetail message
    // (e.g. 409 "Role name X already exists", 403 action not allowed).
    if (error instanceof ResponseError) {
      const errorBody = await error.response
        .clone()
        .json()
        .catch(() => null);
      this.actionError.set(errorBody?.detail ?? errorBody?.title ?? this.translate(fallbackKey));
      return;
    }
    this.actionError.set(this.translate(fallbackKey));
  }

  private translate(key: string): string {
    return this.translateService.instant(key);
  }
}

function requireRoleId(role: Role | null): string {
  if (!role?.id) {
    throw new Error('Role id is required.');
  }
  return role.id;
}

function isPermissionLevel(value: string): value is PermissionLevel {
  return PERMISSION_LEVEL_SET.has(value as PermissionLevel);
}

function createDefaultRoleGrants(): string[] {
  const grants = new Set<string>();
  for (const group of ROLE_PERMISSION_GROUPS) {
    grantsForGroupLevel(group, CREATE_ROLE_DEFAULT_GROUP_LEVELS[group.id]).forEach((grant) =>
      grants.add(grant)
    );
  }
  return [...grants].sort((a, b) => a.localeCompare(b));
}
